import React from "react";
import { motion } from "framer-motion";
import { Users, Radar, Megaphone, Filter, ArrowRight } from "lucide-react";
import PageMeta from "../site/PageMeta";
import Chapter from "../site/Chapter";
import { calendlyUrl, calendlyLinkProps } from "../../lib/calendly";

const services = [
  {
    icon: Users,
    title: "Full Cycle Recruitment",
    body: "End-to-end hiring support — from defining the role to final offer — for positions you need filled without standing up an internal recruiting function.",
  },
  {
    icon: Radar,
    title: "Candidate Sourcing",
    body: "Targeted, AI-assisted outreach to find candidates who aren't actively browsing job boards but are exactly who you're looking for.",
    ai: true,
  },
  {
    icon: Megaphone,
    title: "Skill Marketing",
    body: "Positioning hard-to-fill or specialized roles in a way that attracts the right candidates, not just the most applicants.",
  },
  {
    icon: Filter,
    title: "AI-Assisted Screening & Matching",
    body: "AI-assisted tools screen resumes and shortlist candidates fast — every shortlist is still reviewed by a recruiter before it reaches you.",
    ai: true,
  },
];

const funnel = [
  { n: "130", label: "Candidates longlisted" },
  { n: "91", label: "Shortlisted" },
  { n: "91", label: "Contacted" },
  { n: "7", label: "Moved to screening" },
];

export default function RPO() {
  return (
    <>
      <PageMeta
        title="RPO — Recruitment Process Outsourcing | A4 Assist"
        description="AI-assisted recruiting and human-led screening. End-to-end hiring support, targeted sourcing, and skill marketing — every shortlist reviewed by a recruiter."
      />

      <section className="pt-36 pb-14">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#00E5FF] mb-4">
            RPO — Recruitment Process Outsourcing
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.02em] text-white leading-[1.05] max-w-4xl">
            Recruitment, run like an{" "}
            <span className="font-serif italic">operation</span> —
            <br /> not a favor.
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-400 leading-relaxed">
            Hiring badly is expensive, and hiring slowly is worse. We built our
            recruitment process to move fast without getting careless —
            AI-assisted tools handle the repetitive parts, so our recruiters
            spend their time on the judgment calls that actually decide
            whether a hire works out.
          </p>
        </div>
      </section>

      <section className="pb-24 sm:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 grid grid-cols-1 md:grid-cols-2 gap-5">
          {services.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="rounded-2xl border border-slate-800 bg-[#0A1325]/60 p-7 hover:border-[#00E5FF]/40 transition-colors"
              data-testid={`rpo-service-${i}`}
            >
              <div className="flex items-center gap-3">
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/20">
                  <s.icon size={20} />
                </div>
                {s.ai && <span className="chip-ai">AI-Assisted</span>}
              </div>
              <h3 className="font-display mt-5 text-lg font-semibold text-white">
                {s.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">
                {s.body}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="pb-24 sm:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
          <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-[#0B1A33] to-[#071326] p-8 sm:p-12 relative overflow-hidden">
            <div className="absolute -top-24 -right-16 h-64 w-64 rounded-full bg-[#00E5FF]/10 blur-3xl" />
            <div className="relative">
              <Chapter
                number="→"
                eyebrow="Real Search Snapshot"
                title="What a real search looks like"
                subtitle="One recent client search. Every stage reviewed by a specialist, not just filtered by an algorithm."
              />
              <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
                {funnel.map((f, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.07 }}
                    className="rounded-xl border border-slate-800 bg-[#050B14]/60 p-6 relative"
                    data-testid={`rpo-funnel-${i}`}
                  >
                    <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#00E5FF]">
                      Stage {i + 1}
                    </div>
                    <div className="font-display mt-2 text-4xl font-bold text-white tabular-nums">
                      {f.n}
                    </div>
                    <div className="text-sm text-slate-400 mt-1">{f.label}</div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-28">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 rounded-2xl border border-slate-800 bg-[#0A1325]/60 p-8">
          <div>
            <h3 className="font-display text-xl sm:text-2xl font-semibold text-white">
              Have a role you need filled?
            </h3>
            <p className="mt-1.5 text-sm text-slate-400 max-w-lg">
              Tell us what you're hiring for and we'll come back with a
              recommendation and a shortlist plan.
            </p>
          </div>
          <div className="flex gap-3">
            <a
              href={calendlyUrl({ pillar: "rpo" })}
              {...calendlyLinkProps}
              data-testid="rpo-contact-cta"
              className="group inline-flex items-center gap-2 bg-[#00E5FF] hover:bg-[#00E5FF]/90 text-[#050B14] font-semibold px-6 py-3.5 rounded-md shadow-[0_0_30px_-8px_rgba(0,229,255,0.6)]"
            >
              Get Free Audit <ArrowRight size={16} className="arrow-shift" />
            </a>
            <a
              href="mailto:sahil@a4assist.com?subject=A4%20Assist%20—%20RPO%20inquiry"
              className="inline-flex items-center gap-2 border border-slate-700 hover:border-[#00E5FF]/60 text-white font-semibold px-6 py-3.5 rounded-md"
            >
              Email Sahil
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
